import { type ReactNode } from 'react';

import { useAuthInfo, useLogoutFunction } from '@propelauth/react';

import { OrgPermission } from '../../../../backend/src/organisation/organisation-permission';
import { EnvVars } from '../../../environment';
import { useUserContext } from '../../Contexts/User';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

export function MfaNonSSOGate({ children }: { children: ReactNode }) {
  const { isEnabled } = useOrgPermissions();
  const authInfo = useAuthInfo();
  const { user } = useUserContext();
  const logoutFn = useLogoutFunction();

  const orgRequires2FA = isEnabled(OrgPermission.Require2FANonSSO);
  const isSsoUser = user?.loginMethodType === 'saml_sso';
  const hasMfaEnabled = authInfo.user?.mfaEnabled ?? false;

  if (!user || !authInfo.user) {
    return null;
  }

  if (orgRequires2FA && !isSsoUser && !hasMfaEnabled) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-6 bg-white px-6 text-center">
        <Icon className="lucide--shield-check text-4xl text-gray-400" />

        <div className="max-w-md space-y-2">
          <h1 className="text-xl font-semibold text-gray-900">Two-factor authentication required</h1>
          <p className="text-gray-500">
            Your organisation requires users to enable two-factor authentication. Please set up 2FA to continue using
            the platform.
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="primary" asChild>
            <a href={`${EnvVars.AUTH_URL}/account`} target="_blank" rel="noopener noreferrer">
              <Icon className="lucide--external-link" />
              Set up 2FA
            </a>
          </Button>

          <Button variant="outline" onClick={() => logoutFn(true)}>
            Log out
          </Button>
        </div>
        <p className="text-sm text-gray-400">Refresh this page once you have completed the setup.</p>
      </div>
    );
  }

  return children;
}
